/**
 * Fixtures du mode maquette (`?mock=1`, DEV uniquement — cf. `api.ts`).
 *
 * Contenu réutilisé (paraphrasé fidèlement) de la banque d'items réelle du
 * test — note DOZKA « La Régie — Banque d'items du test (BROUILLON à
 * relire) » — pour que les captures d'écran de vérification montrent du
 * VRAI contenu produit plutôt que du lorem ipsum. 3 cartes d'échauffement +
 * 5 cartes de test (variété des 4 gestes : Envoyer / Corriger / Écarter /
 * Réorienter). Aucune de ces données n'atteint jamais la prod (le flag DEV
 * coupe ce module du bundle de prod par tree-shaking Vite).
 */
import type {
  Availability,
  BaremePoints,
  Card,
  CandidateDetail,
  CandidateListItem,
  CoverageMatrix,
  EvalConfig,
  Gesture,
  GradedCard,
  ReviewItem,
} from "./types";

/** Enlève les champs `__*` (méta de fixture, jamais dans le vrai payload
 *  réseau) avant de "servir" une carte mock au parcours candidat — même
 *  discipline que le vrai backend (réponses attendues jamais envoyées). */
export function toPublicCard(card: Card): Card {
  const {
    id,
    kind,
    fan_name,
    creator_hint,
    thread,
    proposal_message,
    proposal_price_cents,
    proposal_media_label,
    proposal_stage_label,
    is_warmup,
  } = card;
  return {
    id,
    kind,
    fan_name,
    creator_hint,
    thread,
    proposal_message,
    proposal_price_cents,
    proposal_media_label,
    proposal_stage_label,
    is_warmup,
  };
}

// ─── Échauffement (AVEC feedback pédagogique, cf. contrat) ─────────────────

interface MockWarmupCard extends Card {
  /** Geste "vérité" — gardé ICI (fixture mock, jamais dans le vrai payload
   *  réseau) uniquement pour calculer `was_expected` dans `mockAnswer`. */
  __expectedGesture: Gesture;
  __feedback: string;
}

export const MOCK_WARMUP_CARDS: MockWarmupCard[] = [
  {
    id: "mock-warmup-1",
    kind: "clean",
    fan_name: "Yanis",
    creator_hint: "Trista (douce)",
    proposal_stage_label: "Relance",
    is_warmup: true,
    thread: [
      { dir: "in", text: "Coucou toi, ça fait plaisir de te revoir 🥰" },
      { dir: "out", text: "Ohh Yanis 😍 t'étais où tout ce temps ?" },
      { dir: "in", text: "Débordé, mais je pensais à toi. T'as une petite vidéo sympa en ce moment ?" },
    ],
    proposal_message:
      "Haha toujours en train de penser à moi toi 😏 j'ai justement une vidéo toute fraîche, 3 minutes rien que pour te faire sourire, c'est 25€ et elle est à toi dans la seconde",
    proposal_price_cents: 2500,
    proposal_media_label: "Vidéo courte 3 min",
    __expectedGesture: "send",
    __feedback:
      "Bien vu : la proposition répond exactement à sa demande, le prix est dans la grille, et le ton est fidèle à Trista. Rien à changer — c'est exactement ça, un ✓ Envoyer.",
  },
  {
    id: "mock-warmup-2",
    kind: "fault",
    fan_name: "Enzo",
    creator_hint: "Lilou (joueuse)",
    proposal_stage_label: "Réclamation",
    is_warmup: true,
    thread: [
      { dir: "in", text: "Slt, alors cette vidéo que tu m'as dit que t'envoyais hier soir ?" },
      { dir: "out", text: "Héhé désolée la soirée a été folle 🙈" },
      { dir: "in", text: "Bah alors ? Tu me l'envoies ou pas ?" },
    ],
    proposal_message:
      "Mais je te l'ai envoyée hier soir juste après minuit 😏 regarde bien dans la conversation, elle devrait être là !",
    proposal_price_cents: null,
    proposal_media_label: null,
    __expectedGesture: "correct",
    __feedback:
      "Bien vu : elle prétend avoir envoyé une vidéo qui n'existe nulle part dans le fil — c'est exactement le motif 🧾 « Elle invente ». C'est LE motif le plus important, celui qui touche l'argent. Dans le doute sur un contenu, un prix ou un souvenir qui n'existe pas → Corriger + invented.",
  },
  {
    id: "mock-warmup-3",
    kind: "time_waster",
    fan_name: "Karim",
    creator_hint: "Justine (directe)",
    proposal_stage_label: "Demande de gratuit",
    is_warmup: true,
    thread: [
      { dir: "in", text: "Salut cb pour une photo" },
      { dir: "out", text: "Salut toi 😘 j'ai plusieurs formats, tu veux voir lesquels ?" },
      { dir: "in", text: "Nan en fait laisse tomber c'est trop cher tout" },
      { dir: "out", text: "Pas de souci, dis-moi si tu changes d'avis 🙂" },
      { dir: "in", text: "Ouai en fait tu ferais pas ça gratuit vu qu'on discute déjà ?" },
      { dir: "in", text: "Steuplé une seule photo c'est rien pour toi" },
    ],
    proposal_message:
      "Haha t'es mignon de insister 🙈 mais même une photo ça se mérite, tu es sûr que tu veux rien voir dans mon menu ?",
    proposal_price_cents: null,
    proposal_media_label: null,
    __expectedGesture: "discard",
    __feedback:
      "Bien vu : il a déjà dit que c'était trop cher, puis il mendie une photo gratuite — c'est le schéma classique du fan qui ne paiera jamais. Mieux vaut ne pas investir de temps ici → 🚫 Écarter.",
  },
];

// ─── Test (SANS feedback) — variété des 4 gestes ───────────────────────────

interface MockTestCardMeta extends Card {
  /** Méta admin — jamais servie au candidat (cf. `toPublicCard`), utilisée
   *  uniquement par `buildGenericCandidateDetail` pour peupler un détail
   *  admin plausible pour les candidats sans fixture dédiée. */
  __expectedGesture: Gesture;
  __expectedMotif: string | null;
  __rationale: string;
}

export const MOCK_TEST_CARDS: MockTestCardMeta[] = [
  {
    id: "mock-test-1",
    kind: "clean",
    fan_name: "Lucas",
    creator_hint: "Trista (douce)",
    proposal_stage_label: "Closing",
    is_warmup: false,
    thread: [
      { dir: "in", text: "Dis, cette vidéo dont tu parlais l'autre jour, elle coûte combien ?" },
      { dir: "out", text: "Celle où je me filme le matin ? Elle est juste pour toi si tu veux 😘" },
      { dir: "in", text: "Ouais celle-là. Dis-moi ton prix" },
      { dir: "in", text: "Je peux mettre 40 max" },
    ],
    proposal_message: "Va pour 40 alors bb 🥰 je te la prépare et je te l'envoie là, tu vas voir elle vaut le coup",
    proposal_price_cents: 4000,
    proposal_media_label: "Vidéo réveil perso",
    __expectedGesture: "send",
    __expectedMotif: null,
    __rationale: "Le fan a fixé lui-même son prix (40€) ; la proposition s'aligne dessus sans re-négocier.",
  },
  {
    id: "mock-test-2",
    kind: "fault",
    fan_name: "Léo",
    creator_hint: "Trista (douce)",
    proposal_stage_label: "Réclamation",
    is_warmup: false,
    thread: [
      { dir: "out", text: "Ça arrive dans la soirée bb, je te prépare ça 😘" },
      { dir: "in", text: "Alors cette vidéo, elle arrive ?" },
      { dir: "in", text: "Ça fait 10 minutes" },
    ],
    proposal_message: "Mais je viens de te l'envoyer à l'instant bb, regarde bien, elle devrait être juste au-dessus 😘",
    proposal_price_cents: null,
    proposal_media_label: null,
    __expectedGesture: "correct",
    __expectedMotif: "invented",
    __rationale: "Prétend avoir envoyé un contenu qui n'existe nulle part dans le fil.",
  },
  {
    id: "mock-test-3",
    kind: "time_waster",
    fan_name: "Grégory",
    creator_hint: "Justine (directe)",
    proposal_stage_label: "Négociation",
    is_warmup: false,
    thread: [
      { dir: "in", text: "Ta vidéo à 50 je peux avoir à 15 ?" },
      { dir: "out", text: "Ah non ça descend pas jusque là bb, je peux faire 40 max" },
      { dir: "in", text: "35 dernier prix sinon rien" },
      { dir: "out", text: "Bon ok 35 va pour cette fois" },
      { dir: "in", text: "Ah en fait attend, tu peux pas plutôt me faire à 20 direct ?" },
    ],
    proposal_message: "Bon écoute, je peux pas descendre plus, c'est 35 ou rien pour cette vidéo",
    proposal_price_cents: 3500,
    proposal_media_label: "Vidéo (déjà négociée)",
    __expectedGesture: "discard",
    __expectedMotif: "other",
    __rationale: "A déjà obtenu un accord à 35€ puis relance une nouvelle baisse — négociation sans fin.",
  },
  {
    id: "mock-test-4",
    kind: "clean",
    fan_name: "Bastien",
    creator_hint: "Trista (douce)",
    proposal_stage_label: "Relationnel",
    is_warmup: false,
    thread: [
      { dir: "in", text: "Franchement cette semaine grâce à toi j'ai le sourire tous les soirs" },
      { dir: "out", text: "Awww ça me touche énormément 🥰 dis-moi, elle était comment ta journée ?" },
      { dir: "in", text: "Longue mais bonne. Tu sais, je crois que je vais encore craquer pour un truc ce soir, j'ai eu ma paie" },
    ],
    proposal_message: "Haha profites-en alors 😊 raconte-moi ta journée en détail, je veux tout savoir",
    proposal_price_cents: null,
    proposal_media_label: null,
    __expectedGesture: "reorient",
    __expectedMotif: null,
    __rationale: "Signal d'achat explicite (« j'ai eu ma paie ») que la réponse purement relationnelle laisse filer.",
  },
  {
    id: "mock-test-5",
    kind: "fault",
    fan_name: "Samuel",
    creator_hint: "Trista (douce)",
    proposal_stage_label: "Onboarding",
    is_warmup: false,
    thread: [
      { dir: "in", text: "Bonjour madame" },
      { dir: "out", text: "Haha bonjour toi, tu peux me tutoyer tu sais 😊" },
      { dir: "in", text: "Comment allez-vous, quel type de prestation proposez-vous ?" },
    ],
    proposal_message:
      "Bonjour, je vous remercie de votre message. Je propose plusieurs types de contenus, n'hésitez pas à me faire part de vos préférences.",
    proposal_price_cents: null,
    proposal_media_label: null,
    __expectedGesture: "correct",
    __expectedMotif: "robotic_tone",
    __rationale: "Vouvoiement + formules figées (« je vous remercie ») alors que la créatrice tutoie toujours.",
  },
];

// ─── Config & sessions (mock) ───────────────────────────────────────────────

/** Barème gradué par défaut — mêmes valeurs que le défaut serveur (CONTRACT
 *  §1d), pour que l'éditeur admin (Modal seuil + barème) parte d'un état
 *  cohérent avec ce que la migration insérerait. */
export const DEFAULT_BAREME_POINTS: BaremePoints = {
  correction: { correct_good_motif: 100, correct_wrong_motif: 70, correct_no_motif: 60, reorient: 40, discard: 20, send: 0 },
  clean: { send: 100, reorient: 50, correct: 40, discard: 20 },
  discard: { discard: 100, correct: 40, reorient: 40, send: 20 },
  reorient: { reorient: 100, send: 40, correct: 40, discard: 20 },
  dangerous_gesture: "send",
};

export const MOCK_EVAL_CONFIG: EvalConfig = {
  threshold: 80,
  points: DEFAULT_BAREME_POINTS,
  max_sessions: null,
};

/** Scores de session simulés pour `startSession`/`submitSession` en mode
 *  maquette — rotation croissante (façon Josué A.) pour que "Retenter"
 *  montre une vraie progression sur plusieurs clics. */
export const MOCK_SESSION_SCORES: readonly number[] = [58, 74, 82, 88];

/** Revue d'exemple (POST /session/{id}/submit → review) — pédagogie
 *  « Tu as … / Il fallait … », paraphrasée fidèlement de la maquette étape 4
 *  sur deux des cartes de test (invention + time-waster). */
export const MOCK_SESSION_REVIEW: ReviewItem[] = [
  {
    fan_name: "Léo",
    given_label: "Envoyé « je viens de te l'envoyer à l'instant » — alors qu'aucune vidéo n'existe",
    correct_label: "Corriger et dire « elle invente » 🧾",
    why: "Sinon le fan paie pour du vide.",
  },
  {
    fan_name: "Grégory",
    given_label: "Répondu longuement à un fan qui négocie sans fin",
    correct_label: "L'écarter 🚫",
    why: "Ton temps va aux fans qui comptent.",
  },
];

// ─── Admin — passations / couverture / liens ───────────────────────────────

// ─── Disponibilités — 3 candidats-exemples ─────────────────────────────────
// Un seul créneau par candidat (cohérent avec leur `criteria.availability`
// ci-dessous) — suffisant pour peupler la grille dispos de la fiche admin.

const RINA_AVAILABILITY: Availability = {
  mon: ["a"],
  tue: ["a"],
  wed: ["a"],
  thu: ["a"],
  fri: ["a"],
  sat: [],
  sun: [],
};

const JOSUE_AVAILABILITY: Availability = {
  mon: ["c"],
  tue: ["c"],
  wed: ["c"],
  thu: ["c"],
  fri: ["c"],
  sat: ["c"],
  sun: ["c"],
};

const FARA_AVAILABILITY: Availability = {
  mon: ["b"],
  tue: ["b"],
  wed: ["b"],
  thu: ["b"],
  fri: ["b"],
  sat: ["b"],
  sun: ["b"],
};

export const MOCK_CANDIDATES: CandidateListItem[] = [
  {
    id: "cand-rina",
    first_name: "Rina",
    last_name: "M.",
    status: "submitted",
    score: { fault_catch_rate: 1, false_positives: 1, motif_exact: 0.83, dwell_median_ms: 46_000 },
    availability_summary: "Matin ×5",
    source: "telegram",
    source_detail: null,
    gender: "femme",
    age: 27,
    email: "rina.m@example.com",
    contact_discord: "rina_m",
    availability: RINA_AVAILABILITY,
    connection: { verdict: "good", rtt_median_ms: 42, rtt_p95_ms: 78 },
    created_at: "2026-07-14T09:12:00Z",
  },
  {
    id: "cand-josue",
    first_name: "Josué",
    last_name: "A.",
    status: "submitted",
    score: { fault_catch_rate: 12 / 13, false_positives: 1, motif_exact: 0.83, dwell_median_ms: 37_000 },
    availability_summary: "Soir ×7",
    source: "facebook",
    source_detail: null,
    gender: "homme",
    age: 22,
    email: "josue.a@example.com",
    contact_discord: "josue.a",
    availability: JOSUE_AVAILABILITY,
    connection: { verdict: "ok", rtt_median_ms: 180, rtt_p95_ms: 430 },
    created_at: "2026-07-14T08:40:00Z",
  },
  {
    id: "cand-fara",
    first_name: "Fara",
    last_name: "R.",
    status: "submitted",
    score: { fault_catch_rate: 7 / 13, false_positives: 5, motif_exact: 0.33, dwell_median_ms: 20_000 },
    availability_summary: "Après-midi ×7",
    source: "telegram",
    source_detail: null,
    gender: "femme",
    age: 34,
    email: "fara.r@example.com",
    contact_discord: null,
    availability: FARA_AVAILABILITY,
    connection: { verdict: "bad", rtt_median_ms: 720, rtt_p95_ms: 1_900 },
    created_at: "2026-07-14T07:05:00Z",
  },
  {
    id: "cand-camille",
    first_name: "Camille",
    last_name: "D.",
    status: "submitted",
    score: { fault_catch_rate: 0.92, false_positives: 1, motif_exact: 0.83, dwell_median_ms: 14 * 60_000 },
    availability_summary: "Soir ×5 · Ap-m ×3",
    gender: "femme",
    age: 25,
    email: "camille.d@example.com",
    contact_discord: "camille_d",
    availability: JOSUE_AVAILABILITY,
    connection: { verdict: "good", rtt_median_ms: 31, rtt_p95_ms: 60 },
    created_at: "2026-07-11T09:12:00Z",
  },
  {
    id: "cand-yanis",
    first_name: "Yanis",
    last_name: "B.",
    status: "submitted",
    score: { fault_catch_rate: 0.83, false_positives: 3, motif_exact: 0.67, dwell_median_ms: 21 * 60_000 },
    availability_summary: "Matin ×4 · Ap-m ×5",
    gender: "homme",
    age: 19,
    email: "yanis.b@example.com",
    contact_discord: "yanisb",
    availability: RINA_AVAILABILITY,
    connection: { verdict: "good", rtt_median_ms: 88, rtt_p95_ms: 140 },
    created_at: "2026-07-11T08:40:00Z",
  },
  {
    id: "cand-sofia",
    first_name: "Sofia",
    last_name: "L.",
    status: "submitted",
    score: { fault_catch_rate: 0.67, false_positives: 0, motif_exact: 0.75, dwell_median_ms: 26 * 60_000 },
    availability_summary: "Soir ×6",
    gender: "femme",
    age: 41,
    email: "sofia.l@example.com",
    contact_discord: "sofial",
    availability: FARA_AVAILABILITY,
    connection: { verdict: "ok", rtt_median_ms: 210, rtt_p95_ms: 520 },
    created_at: "2026-07-10T19:05:00Z",
  },
  {
    id: "cand-kevin",
    first_name: "Kevin",
    last_name: "M.",
    status: "submitted",
    score: { fault_catch_rate: 0.42, false_positives: 7, motif_exact: 0.33, dwell_median_ms: 6 * 60_000 },
    availability_summary: "Matin ×3",
    gender: "homme",
    age: 29,
    email: "kevin.m@example.com",
    contact_discord: null,
    availability: RINA_AVAILABILITY,
    connection: { verdict: "bad", rtt_median_ms: 940, rtt_p95_ms: 2_400 },
    created_at: "2026-07-10T14:22:00Z",
  },
  {
    id: "cand-ines",
    first_name: "Inès",
    last_name: null,
    status: "started",
    score: null,
    availability_summary: null,
    gender: null,
    age: null,
    email: "ines@example.com",
    contact_discord: null,
    availability: null,
    connection: null,
    created_at: "2026-07-11T10:02:00Z",
  },
  {
    id: "cand-marc",
    first_name: null,
    last_name: null,
    status: "created",
    score: null,
    availability_summary: null,
    gender: null,
    age: null,
    email: null,
    contact_discord: null,
    availability: null,
    connection: null,
    created_at: "2026-07-10T17:30:00Z",
  },
];

/**
 * Les 3 candidats-exemples EXACTS de la maquette validée (fidélité
 * obligatoire — critères, courbes, fault_learning, cartes graduées, rythme,
 * drapeaux repris tels quels, cf. script `CANDS` de la maquette) :
 *   - Rina M.   : 74→88, recrutable, apprend vite, ordi + bonne connexion.
 *   - Josué A.  : 58→76→85, vivier (mobile « à confirmer »), apprend vite.
 *   - Fara R.   : 5 sessions 54→52→55→58→57, stagne, 🚩 invente encore à S5.
 */
export const MOCK_CANDIDATE_DETAIL: Record<string, CandidateDetail> = {
  "cand-rina": {
    gender: "femme",
    birth_date: "1999-03-12",
    age: 27,
    contact_discord: "rina_m",
    first_name: "Rina",
    last_name: "M.",
    email: "rina.m@example.com",
    timezone: "Africa/Antananarivo",
    start_availability: "now",
    work_situation: "free",
    availability: RINA_AVAILABILITY,
    criteria: {
      computer: { status: "ok", label: "Ordinateur", detail: "Windows · souris détectée" },
      connection: { status: "ok", label: "Connexion", detail: "Bonne — 190 ms · 99 %" },
      availability: { status: "ok", label: "Dispos sur tes créneaux", detail: "Matin (shift A)" },
    },
    verdict: {
      recruitable: true,
      reason: "",
      suggestion: { cls: "ok", txt: "✅ Recrutable — et a appris vite" },
    },
    sessions: [
      { index: 1, score: 74, reached: false },
      { index: 2, score: 88, reached: true },
    ],
    sessions_to_threshold: 2,
    best_score: 88,
    fault_learning: { dangerous_sent: [1, 0], false_positives: [3, 1], improving: true },
    flags: [],
    rhythm: {
      consignes_ms: 3 * 60_000 + 40_000,
      session_ms: 14 * 60_000 + 20_000,
      dwell_median_ms: 46_000,
      reading: "A lu les consignes · rythme régulier.",
    },
    cards: [
      { n: 3, kind: "faute · invente", expected_label: "Corrige + « elle invente »", given_label: "Corrige + « elle invente »", points: 100, is_dangerous: false, note: null },
      { n: 5, kind: "propre", expected_label: "Envoie tel quel", given_label: "Envoie tel quel", points: 100, is_dangerous: false, note: null },
      { n: 7, kind: "faute · pas français", expected_label: "Corrige + « incohérent »", given_label: "Corrige + « incohérent »", points: 100, is_dangerous: false, note: null },
      { n: 9, kind: "time-waster", expected_label: "Écarte + motif", given_label: "Écarte + motif", points: 100, is_dangerous: false, note: null },
      { n: 11, kind: "faute · ton robot", expected_label: "Corrige + « ton robot »", given_label: "Corrige + « autre »", points: 70, is_dangerous: false, note: "A attrapé, motif imprécis" },
      { n: 14, kind: "vente 2 étages", expected_label: "Envoie (cohérent)", given_label: "Envoie (cohérent)", points: 100, is_dangerous: false, note: null },
    ],
    // Le premier candidat du mock porte l'ÉPREUVE DE VENTE : c'est la forme
    // que rendent les candidatures depuis le 18-08, et le harnais admin doit
    // la montrer par défaut. Les suivants gardent leurs cartes de décision —
    // les deux cohabitent en base, et la fiche doit savoir rendre les deux.
    vente: {
      criteres: [
        { cle: "ultimatum", libelle: "Il annonce que la suite est payante", reussi: true },
        { cle: "tarifs", libelle: "Il propose les tarifs au lieu d'attendre", reussi: true },
        { cle: "menu", libelle: "Il envoie le menu avec /menu", reussi: true },
        { cle: "offre", libelle: "Il sait dire ce qu'il y a dans l'offre", reussi: true },
        { cle: "objection_prix", libelle: "Il répond au « c'est cher »", reussi: true },
        { cle: "objection_apercu", libelle: "Il répond au « je peux voir avant ? »", reussi: false },
        { cle: "tx_envoyee", libelle: "Il monte une transaction", reussi: true },
        { cle: "tx_offre", libelle: "Il choisit l'offre dans le catalogue", reussi: true, note: "Vidéo solo" },
        { cle: "tx_prix", libelle: "Le prix tient dans la fourchette de l'offre", reussi: true, note: "50 €" },
        { cle: "tx_teaser", libelle: "Un teaser à voir avant de payer", reussi: true, note: "1 média" },
        { cle: "tx_livraison", libelle: "Le contenu est livré au paiement", reussi: false, note: "rien à livrer — le fan paie et ne reçoit rien" },
        { cle: "rendre", libelle: "Il rend la main à Cyrano", reussi: true },
      ],
      reussis: 10,
      total: 12,
      duree_secondes: 412,
      gestes: [
        { etape: "ultimatum", type: "dire", texte: "la vérité je suis grave chaude, mais si tu paies pas on stop maintenant", source: "ecrit", a_ms: 12_000, reussi: true },
        { etape: "tarifs", type: "dire", texte: "j'ai des nudes, des vidéos solo, des vidéos avec un jouet… tu veux les tarifs ?", source: "script_modifie", a_ms: 48_000, reussi: true },
        { etape: "menu", type: "menu", texte: "", source: null, a_ms: 71_000, reussi: true },
        { etape: "expliquer", type: "dire", texte: "pour 50 € t'as une vidéo bien longue, trois nudes, et je te chauffe jusqu'au bout", source: "script", a_ms: 119_000, reussi: true },
        { etape: "objection_prix", type: "dire", texte: "j'suis sans limite ce soir, j'vais tout faire pour pas te décevoir", source: "ecrit", a_ms: 186_000, reussi: true },
        { etape: "transaction", type: "transaction", texte: "", source: null, a_ms: 352_000, reussi: true },
      ],
      ses_messages: [
        "la vérité je suis grave chaude, mais si tu paies pas on stop maintenant",
        "j'ai des nudes, des vidéos solo, des vidéos avec un jouet… tu veux les tarifs ?",
        "pour 50 € t'as une vidéo bien longue, trois nudes, et je te chauffe jusqu'au bout",
        "j'suis sans limite ce soir, j'vais tout faire pour pas te décevoir",
        "j'te mets ça, tu payes et j't'envoie tout 😏",
      ],
    },
    device: { cls: "computer", label: "Ordinateur", detail: "Windows · souris · testé sur PC", pill: null },
    connection: { verdict: "good", rtt_median_ms: 190, rtt_p95_ms: 230 },
  },
  "cand-josue": {
    gender: "homme",
    birth_date: "2004-01-08",
    age: 22,
    contact_discord: "josue.a",
    first_name: "Josué",
    last_name: "A.",
    email: "josue.a@example.com",
    timezone: "Africa/Porto-Novo",
    start_availability: "this_week",
    work_situation: "student",
    availability: JOSUE_AVAILABILITY,
    criteria: {
      computer: { status: "bad", label: "Ordinateur", detail: "Non — testé sur mobile" },
      connection: { status: "warn", label: "Connexion", detail: "Correcte — 640 ms" },
      availability: { status: "ok", label: "Dispos sur tes créneaux", detail: "Soir (shift C)" },
    },
    verdict: {
      recruitable: false,
      reason: "Ordinateur non confirmé (testé sur mobile)",
      suggestion: { cls: "warn", txt: "🟡 Vivier — a appris vite, confirmer l'ordinateur" },
    },
    sessions: [
      { index: 1, score: 58, reached: false },
      { index: 2, score: 76, reached: false },
      { index: 3, score: 85, reached: true },
    ],
    sessions_to_threshold: 3,
    best_score: 85,
    fault_learning: { dangerous_sent: [2, 1, 0], false_positives: [4, 2, 1], improving: true },
    flags: [],
    rhythm: {
      consignes_ms: 2 * 60_000 + 30_000,
      session_ms: 11 * 60_000 + 5_000,
      dwell_median_ms: 37_000,
      reading: "A relu la revue · corrige ses fautes d'une session à l'autre.",
    },
    cards: [
      { n: 2, kind: "faute · invente", expected_label: "Corrige + « elle invente »", given_label: "Corrige + « elle invente »", points: 100, is_dangerous: false, note: "Session 1 il l'envoyait — corrigé depuis" },
      { n: 4, kind: "propre", expected_label: "Envoie tel quel", given_label: "Envoie tel quel", points: 100, is_dangerous: false, note: null },
      { n: 6, kind: "time-waster", expected_label: "Écarte + motif", given_label: "Écarte + motif", points: 100, is_dangerous: false, note: null },
      { n: 10, kind: "faute · pas français", expected_label: "Corrige + « incohérent »", given_label: "Corrige + « incohérent »", points: 100, is_dangerous: false, note: null },
      { n: 12, kind: "faute · ton robot", expected_label: "Corrige + « ton robot »", given_label: "Corrige + « autre »", points: 70, is_dangerous: false, note: "A attrapé, motif imprécis" },
      { n: 15, kind: "vente 2 étages", expected_label: "Envoie (cohérent)", given_label: "Envoie (cohérent)", points: 100, is_dangerous: false, note: null },
    ],
    // 🩸 Josué est sur MOBILE avec sa pastille : c'est le cas EXACT qui a
    // crashé la fiche en prod le 18-08. Il reste ici pour qu'on retombe
    // dessus en `?mock=1` sans attendre le bon candidat.
    device: {
      cls: "mobile",
      label: "Mobile — pas d'ordinateur confirmé",
      detail: "Android · tactile · testé sur téléphone",
      pill: "À confirmer",
    },
    connection: { verdict: "ok", rtt_median_ms: 640, rtt_p95_ms: 900 },
  },
  "cand-fara": {
    gender: "femme",
    birth_date: "1992-06-25",
    age: 34,
    contact_discord: null,
    first_name: "Fara",
    last_name: "R.",
    email: "fara.r@example.com",
    timezone: "Africa/Antananarivo",
    start_availability: "later",
    work_situation: "employed",
    availability: FARA_AVAILABILITY,
    criteria: {
      computer: { status: "ok", label: "Ordinateur", detail: "Windows · souris détectée" },
      connection: { status: "ok", label: "Connexion", detail: "Bonne — 210 ms · 98 %" },
      availability: { status: "ok", label: "Dispos sur tes créneaux", detail: "Après-midi (shift B)" },
    },
    verdict: {
      recruitable: false,
      reason: "Seuil jamais atteint en 5 sessions ; fautes dangereuses répétées",
      suggestion: { cls: "bad", txt: "🔴 Écarter — n'apprend pas, stagne sous le seuil" },
    },
    sessions: [
      { index: 1, score: 54, reached: false },
      { index: 2, score: 52, reached: false },
      { index: 3, score: 55, reached: false },
      { index: 4, score: 58, reached: false },
      { index: 5, score: 57, reached: false },
    ],
    sessions_to_threshold: null,
    best_score: 58,
    fault_learning: { dangerous_sent: [2, 2, 1, 2, 2], false_positives: [5, 4, 5, 4, 5], improving: false },
    flags: ["Envoie encore une invention à la session 5 — malgré les revues"],
    rhythm: {
      consignes_ms: 40_000,
      session_ms: 6 * 60_000 + 10_000,
      dwell_median_ms: 20_000,
      reading: "A survolé les consignes ET les revues · mêmes fautes à chaque session.",
    },
    cards: [
      { n: 1, kind: "faute · invente", expected_label: "Corrige + « elle invente »", given_label: "Envoie tel quel", points: 0, is_dangerous: true, note: "⚠ Danger — encore à la session 5" },
      { n: 3, kind: "propre", expected_label: "Envoie tel quel", given_label: "Corrige (faux positif)", points: 40, is_dangerous: false, note: "Message déjà bon" },
      { n: 5, kind: "propre", expected_label: "Envoie tel quel", given_label: "Écarte", points: 10, is_dangerous: false, note: "A viré un bon fan" },
      { n: 8, kind: "time-waster", expected_label: "Écarte + motif", given_label: "Envoie une offre", points: 20, is_dangerous: false, note: null },
      { n: 11, kind: "faute · pas français", expected_label: "Corrige + « incohérent »", given_label: "Corrige + « elle invente »", points: 65, is_dangerous: false, note: "A attrapé, mauvais motif" },
      { n: 13, kind: "vente 2 étages", expected_label: "Envoie (cohérent)", given_label: "Envoie (incohérent)", points: 15, is_dangerous: false, note: "Prix / média incohérents" },
    ],
    device: { cls: "computer", label: "Ordinateur", detail: "Windows · souris · testé sur PC", pill: null },
    connection: { verdict: "good", rtt_median_ms: 210, rtt_p95_ms: 260 },
  },
};

// ─── Couverture des shifts ──────────────────────────────────────────────────

export const MOCK_COVERAGE: CoverageMatrix = {
  mon: {
    a: [{ candidate_id: "cand-yanis", first_name: "Yanis", score: 0.83 }],
    b: [
      { candidate_id: "cand-camille", first_name: "Camille", score: 0.92 },
      { candidate_id: "cand-yanis", first_name: "Yanis", score: 0.83 },
    ],
    c: [
      { candidate_id: "cand-camille", first_name: "Camille", score: 0.92 },
      { candidate_id: "cand-sofia", first_name: "Sofia", score: 0.67 },
    ],
    d: [],
  },
  tue: {
    a: [],
    b: [{ candidate_id: "cand-yanis", first_name: "Yanis", score: 0.83 }],
    c: [{ candidate_id: "cand-camille", first_name: "Camille", score: 0.92 }],
    d: [],
  },
  wed: {
    a: [{ candidate_id: "cand-yanis", first_name: "Yanis", score: 0.83 }],
    b: [
      { candidate_id: "cand-camille", first_name: "Camille", score: 0.92 },
      { candidate_id: "cand-yanis", first_name: "Yanis", score: 0.83 },
    ],
    c: [{ candidate_id: "cand-sofia", first_name: "Sofia", score: 0.67 }],
    d: [],
  },
  thu: {
    a: [],
    b: [{ candidate_id: "cand-yanis", first_name: "Yanis", score: 0.83 }],
    c: [
      { candidate_id: "cand-camille", first_name: "Camille", score: 0.92 },
      { candidate_id: "cand-sofia", first_name: "Sofia", score: 0.67 },
    ],
    d: [],
  },
  fri: {
    a: [{ candidate_id: "cand-yanis", first_name: "Yanis", score: 0.83 }],
    b: [{ candidate_id: "cand-camille", first_name: "Camille", score: 0.92 }],
    c: [
      { candidate_id: "cand-camille", first_name: "Camille", score: 0.92 },
      { candidate_id: "cand-sofia", first_name: "Sofia", score: 0.67 },
    ],
    d: [],
  },
  sat: {
    a: [],
    b: [],
    c: [{ candidate_id: "cand-sofia", first_name: "Sofia", score: 0.67 }],
    d: [],
  },
  sun: {
    a: [],
    b: [{ candidate_id: "cand-camille", first_name: "Camille", score: 0.92 }],
    c: [{ candidate_id: "cand-sofia", first_name: "Sofia", score: 0.67 }],
    d: [],
  },
};

export const MOCK_ITEMS_ACTIVE_COUNT = 47;

/**
 * Détail admin plausible pour un candidat SANS fixture dédiée (tous sauf les
 * 3 candidats-exemples, cf. `MOCK_CANDIDATE_DETAIL`) — dérivé des cartes de
 * test + du score legacy de la liste (`CandidateScore`), pour que cliquer
 * n'importe quelle ligne en mode maquette reste montrable sans jamais
 * planter. Correction déterministe (pas de vrai aléatoire) : les
 * `round(fault_catch_rate × N)` premières cartes sont "correctes" (100 pts),
 * le reste "manqué" (0 pts, danger si carte fautive) — assez pour illustrer
 * la fiche graduée, pas une vraie simulation de scoring multi-session.
 */
export function buildGenericCandidateDetail(candidate: CandidateListItem): CandidateDetail {
  const rate = candidate.score?.fault_catch_rate ?? 0.5;
  const correctCount = Math.round(rate * MOCK_TEST_CARDS.length);
  const dwellMedian = candidate.score?.dwell_median_ms ?? 20_000;
  const bestScore = Math.round(rate * 100);
  const threshold = MOCK_EVAL_CONFIG.threshold;
  const reached = bestScore >= threshold;
  const computerOk = candidate.status !== "created";
  const connectionOk = (candidate.score?.false_positives ?? 0) <= 2;
  const availabilityOk = candidate.availability_summary != null;

  const cards: GradedCard[] = MOCK_TEST_CARDS.map((card, i) => {
    const isCorrect = i < correctCount;
    const kindLabel =
      card.kind === "fault"
        ? "faute"
        : card.kind === "time_waster"
          ? "time-waster"
          : card.kind === "sale_2stage"
            ? "vente 2 étages"
            : "propre";
    const expectedLabel =
      card.__expectedGesture === "correct"
        ? `Corrige${card.__expectedMotif ? ` + « ${card.__expectedMotif} »` : ""}`
        : card.__expectedGesture === "discard"
          ? "Écarte + motif"
          : card.__expectedGesture === "reorient"
            ? "Réoriente"
            : "Envoie tel quel";
    const isDangerous = !isCorrect && card.kind === "fault" && card.__expectedGesture !== "send";
    return {
      n: i + 1,
      kind: kindLabel,
      expected_label: expectedLabel,
      given_label: isCorrect ? expectedLabel : isDangerous ? "Envoie tel quel" : "Corrige (faux positif)",
      points: isCorrect ? 100 : isDangerous ? 0 : 40,
      is_dangerous: isDangerous,
      note: isCorrect ? null : (card.__rationale ?? null),
    };
  });

  return {
    first_name: candidate.first_name ?? "—",
    last_name: candidate.last_name ?? "",
    email: "—",
    timezone: "Europe/Paris",
    start_availability: "this_week",
    work_situation: "free",
    availability: FARA_AVAILABILITY,
    criteria: {
      computer: computerOk
        ? { status: "ok", label: "Ordinateur", detail: "Testé sur PC" }
        : { status: "bad", label: "Ordinateur", detail: "Pas encore vérifié" },
      connection: connectionOk
        ? { status: "ok", label: "Connexion", detail: "Correcte" }
        : { status: "warn", label: "Connexion", detail: "Instable par moments" },
      availability: availabilityOk
        ? { status: "ok", label: "Dispos sur tes créneaux", detail: String(candidate.availability_summary) }
        : { status: "bad", label: "Dispos sur tes créneaux", detail: "Aucun créneau" },
    },
    verdict: {
      recruitable: computerOk && connectionOk && availabilityOk && reached,
      reason: reached ? "" : "Seuil pas encore atteint",
      suggestion: reached
        ? { cls: "ok", txt: "✅ Recrutable" }
        : { cls: "warn", txt: "🟡 Vivier — continue de s'entraîner" },
    },
    sessions: [{ index: 1, score: bestScore, reached }],
    sessions_to_threshold: reached ? 1 : null,
    best_score: bestScore,
    fault_learning: { dangerous_sent: [], false_positives: [], improving: true },
    flags: [],
    rhythm: {
      consignes_ms: 60_000,
      session_ms: dwellMedian * MOCK_TEST_CARDS.length,
      dwell_median_ms: dwellMedian,
      reading: "Rythme non détaillé (fiche générique).",
    },
    cards,
    device: computerOk
      ? { cls: "computer", label: "Ordinateur", detail: "Testé sur PC", pill: null }
      : { cls: "mobile", label: "Appareil non confirmé", detail: "—", pill: "À confirmer" },
    connection: { verdict: connectionOk ? "good" : "ok", rtt_median_ms: 400, rtt_p95_ms: 600 },
  };
}
